<script lang="ts" setup>
import { Head, Link, usePage } from "@inertiajs/vue3";
import JetCheckbox from "@/Components/Checkbox.vue";
import JetLabel from "@/Components/InputLabel.vue";

import HeadingComponent from "../../Components/HeadingComponent.vue";
import EventPublicWrapper from "../EventPublic/EventPublicWrapper.vue";
import { onMounted, onUnmounted, ref } from "vue";
import moment from "moment";
import momentDurationFormatSetup from "moment-duration-format";

momentDurationFormatSetup(moment);

import { Event } from "@/types";
import "/node_modules/flag-icons/css/flag-icons.min.css";
import ReplayIcon from "@/Components/assets/ReplayIcon.vue";
import TooltipIcon from "@/Components/TooltipIcon.vue";
import EventClassesFilter from "@/Pages/EventPublic/Components/EventClassesFilter.vue";
import EventSectionsFilter from "@/Pages/EventPublic/Components/EventSectionsFilter.vue";
import CcsPenaltiesTooltipContent from "@/Pages/EventPublic/Components/CcsPenaltiesTooltipContent.vue";
import EditIcon from "@/Components/assets/EditIcon.vue";
import CompetitorClassSectionPenaltiesFormModal from "@/Pages/EventPublic/Components/CompetitorClassSectionPenaltiesFormModal.vue";
import eventBus from "@/Helpers/eventBus";
import FullscreenIcon from "@/Components/assets/FullscreenIcon.vue";
import { DateTime } from "luxon";
import FwbTooltip from "@/Components/FwbTooltip/FwbTooltip.vue";
import Button from "@/Components/Button.vue";
import { convertKmAsPerTrackUnits, formatUnitsHuman, printElement } from "@/Helpers/helperFunctions";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { startLocalEventTimeTicker } from "@/Helpers/localEventTimeTicker";

interface Props {
    event: Event;
    canRegisterForEvent: boolean;
    event_results: object;
    overall_results: object;
}

const startListClassSelected = ref(null);
const startListSectionSelected = ref(null);

const props = withDefaults(defineProps<Props>(), {});

function showCcsPenaltiesFormModal(ccs, classSection) {
    eventBus.$emit("showCcsPenaltiesFormModal", {
        ccs: ccs,
        useAxios: false,
        trackUnits: findTrackByClassSectionId(classSection.id).units,
    });
}

function getSections(eventClass, startListSectionSelected) {
    return startListSectionSelected !== null
        ? eventClass.classSections.filter((cs) => cs.id === startListSectionSelected)
        : eventClass.classSections;
}

function findTrackByClassSectionId(class_section_id) {
    const cs = props.event_results.classSections.find((cs) => cs.id == class_section_id);
    return cs.track;
}

function filterClassSectionWaypoints(classSection) {
    return classSection.waypoints;
    if (!classSection.filter_only_dn) {
        return classSection.waypoints;
    }

    let ccw = classSection.waypoints;

    ccw = ccw.filter((csw) => {
        // console.log(c.event_class_id);
        return csw.types.includes("DSS") || csw.types.includes("ASS") || csw.types.includes("DN");
    });

    return ccw;
}

function findCompetitorClassSectionWaypoint(competitorClassSection, wpt, field = "in_time") {
    const foundCompetitorWpt = competitorClassSection.class_section_instructions.find((csInstruction) => {
        if (csInstruction.id === wpt.id) {
            return csInstruction;
        }
    });

    if (foundCompetitorWpt) {
        if (wpt.types.includes("DSS")) {
            if (field === "in_time" || field === "out_time") {
                return "00:00:00";
            }
            return DateTime.fromISO(competitorClassSection.start_at)
                .setZone(usePage().props.event.timezone)
                .toLocaleString(DateTime.TIME_24_WITH_SECONDS);
        }

        if (field === "in_time" || field === "out_time") {
            return moment
                .duration(foundCompetitorWpt.pivot[field], "milliseconds")
                .format("hh:mm:ss", { trim: false });
        }

        // console.log(foundCompetitorWpt.pivot[field]);

        const clockTime = new Date(foundCompetitorWpt.pivot[field]);
        const ms = clockTime.getMilliseconds();
        // round seconds up if needed
        if (ms >= 500) {
            clockTime.setSeconds(clockTime.getSeconds() + 1);
        }

        return DateTime.fromJSDate(clockTime)
            .setZone(usePage().props.event.timezone)
            .toLocaleString(DateTime.TIME_24_WITH_SECONDS);
    }

    return "-";
}

function getOverallCompetitors(event_class_id) {
    let ccc = usePage()
        .props.competitors.filter((c) => {
            // console.log(c.event_class_id);
            return event_class_id ? c.event_class_id === event_class_id : true;
        })
        .map((c) => {
            Object.assign(c, getCompetitorOverallTime(c));
            // console.log(c);
            return c;
        })
        .filter((c) => c.overall_duration_s && !c.overall_state)
        .sort(function compareFn(a, b) {
            if (props.event.id != 67) {
                // TODO: Alakonnu ir problēma ar šo...
                if (a.overall_section_count < b.overall_section_count) {
                    return 1;
                }
                if (a.overall_section_count > b.overall_section_count) {
                    return -1;
                }
            }

            if (a.overall_duration_s < b.overall_duration_s) {
                return -1;
            }
            if (a.overall_duration_s > b.overall_duration_s) {
                return 1;
            }
            // a must be equal to b
            return 0;
        });

    let currentMaxSectionsCount = 0;
    if (ccc.length) {
        currentMaxSectionsCount = ccc[0].overall_section_count;
    }

    // rādam tikai tos, kas nobraukuši visvairāk sekcijas, un vienādu to skaitu.
    // tādā veidā nerādam tos, kas vēl nav nobraukuši vienādu skaitu sekciju
    if (props.event.id != 67) {
        // TODO: Alakonnu ir problēma ar šo...
        ccc = ccc.filter((c) => c.overall_section_count === currentMaxSectionsCount);
    }

    return ccc;
}

function getCompetitorOverallTime(competitor) {
    let duration = null;
    let bonus_s = 0;
    let total_penalties_s = 0;
    let state = null;
    let overall_section_count = 0;
    for (let i2 = 0, k2 = Object.keys(props.event_results.classes), l2 = k2.length; i2 < l2; i2++) {
        const eventClass = props.event_results.classes[k2[i2]];

        for (let i = 0, l = eventClass.classSections.length; i < l; i++) {
            const cs = eventClass.classSections[i];

            const css = cs.competitorClassSections.find((c) => c.competitor_id === competitor.id);
            if (css && css.state) {
                state = css.state;
            }
            if (css && css.time && css.total_time) {
                overall_section_count++;
                if (!cs.is_add_to_overall_results) {
                    // šis bloks ir te, lai paspēj pieskaitīt overall_section_count
                    // ja vienai klasei prologu skaita klāt, bet otrai nē, tad ir problēma.
                    // tpc vjag pieskaitīt overall_section_count arī sekcijām, kuras neskaita kopvērtējumā.
                    // tas vajadzīgs, lai visu klašu overall korekti darbotos.
                    continue;
                }
                if (!duration) {
                    duration = moment.duration(css.total_time, "milliseconds");
                } else {
                    duration = duration.add(moment.duration(css.total_time, "milliseconds"));
                }

                if (css.bonus_s) {
                    bonus_s += css.bonus_s;
                }

                if (css.total_penalties_s) {
                    total_penalties_s += css.total_penalties_s;
                }
            }
        }
    }

    if (!duration) {
        return {};
    }

    return {
        overall_duration_s: Math.round(duration.as("seconds")),
        overall_duration: duration.format("hh:mm:ss", { trim: false }),
        overall_bonus_s: bonus_s,
        overall_total_penalties_s: total_penalties_s,
        overall_state: state,
        overall_section_count: overall_section_count,
    };
}

function shouldShowCcsPenaltiesTooltip(ccs) {
    return ccs.bonus_s > 0 || ccs.penalty_s > 0 || ccs.penalties.length > 0;
}

const disclosure = ref([]);

const infos = ref([]);
infos.value.push({
    title: "OVERALL",
});

for (let i = 0, l = props.event_results.classes.length; i < l; i++) {
    infos.value.push({
        title: props.event_results.classes[i].name,
    });
}

const expandAll = (idx) => {
    console.log("disclosure.value", disclosure.value);
    disclosure.value.forEach((open) => open());
};

function getSectionDistance(section) {
    // console.log("getSectionDistance", section);
    return usePage().props.track_distances[section.track_version_id];
}

onMounted(() => {});

onUnmounted(() => {});
</script>

<template>
    <EventPublicWrapper>
        <Head>
            <title>Event results: {{ $page.props.event.name }}</title>
            <meta
                property="og:title"
                head-key="og:title"
                :content="`Event results: ${$page.props.meta_event_name}`"
            />
            <link
                rel="canonical"
                :href="
                    route('event.public.results', {
                        hashid: $page.props.event.hashid,
                        seo_slug: $page.props.event.seo_slug,
                    })
                "
            />
        </Head>
        <div class="flex-1 space-y-4 overflow-auto md:overflow-visible">
            <div class="max-w-7xl space-y-4">
                <HeadingComponent class="rounded-lg bg-white p-4 shadow-lg sm:px-6">
                    <template #name>Results</template>
                    <template #description>
                        <div v-if="!event.is_started">Event not started yet.</div>
                        <div v-if="event.is_started">
                            <Button
                                color="secondary"
                                type="button"
                                class="h-6"
                                href="?with-all-waypoints=1"
                            >
                                Show results including waypoints
                            </Button>
                            <a
                                v-if="$page.props.can.view_sensitive_event"
                                class="inline-flex pl-4 text-right"
                                href="?excel=1"
                                >Results as Excel</a
                            >
                            <!--                            <Button
                                color="secondary"
                                class="h-6"
                                type="button"
                                @click="expandAll()"
                            >
                                Expand all
                            </Button>-->
                        </div>
                    </template>
                </HeadingComponent>

                <!--                <EventClassesFilter
                    :start-list-class-selected="startListClassSelected"
                    :start-list-section-selected="startListSectionSelected"
                    :event-classes="event_results.classes"
                    @set-start-list-class-selected="
                        (val) => {
                            startListClassSelected = val;
                        }
                    "
                    @set-start-list-section-selected="
                        (val) => {
                            startListSectionSelected = val;
                        }
                    "
                ></EventClassesFilter>

                <EventSectionsFilter
                    :start-list-class-selected="startListClassSelected"
                    :start-list-section-selected="startListSectionSelected"
                    :event-class-sections="event_results.classSections"
                    @set-start-list-section-selected="
                        (val) => {
                            startListSectionSelected = val;
                        }
                    "
                ></EventSectionsFilter>-->
            </div>
            <div
                v-if="!event.is_started"
                class="border-b bg-gray-50 p-4 font-bold"
            >
                Event not started. No results to display.
            </div>
            <div class="flex w-full flex-col space-y-4 overflow-auto md:w-auto">
                <ul
                    v-if="event.is_started"
                    class="flex flex-col space-y-4"
                >
                    <Disclosure
                        v-for="(info, idx) in infos"
                        :key="idx"
                        v-slot="{ open, close }"
                        as="li"
                        class="overflow-hidden rounded-lg bg-white shadow-lg"
                    >
                        <DisclosureButton
                            :id="'classss-' + idx"
                            :ref="(el) => (disclosure[idx] = close)"
                            as="h2"
                            class="flex cursor-pointer flex-row items-center p-3 sm:p-4"
                        >
                            <span
                                class="p-4 px-1 text-left text-2xl leading-7 text-blue-600 visited:text-violet-500 hover:underline"
                                >{{ info.title }}</span
                            >
                            <span class="ml-6 flex h-7 items-center">
                                <svg
                                    :class="open ? 'rotate-180 duration-500' : 'rotate-0 duration-500'"
                                    class="size-8 fill-current text-blue-700"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        d="M13.962,8.885l-3.736,3.739c-0.086,0.086-0.201,0.13-0.314,0.13S9.686,12.71,9.6,12.624l-3.562-3.56C5.863,8.892,5.863,8.611,6.036,8.438c0.175-0.173,0.454-0.173,0.626,0l3.25,3.247l3.426-3.424c0.173-0.172,0.451-0.172,0.624,0C14.137,8.434,14.137,8.712,13.962,8.885 M18.406,10c0,4.644-3.763,8.406-8.406,8.406S1.594,14.644,1.594,10S5.356,1.594,10,1.594S18.406,5.356,18.406,10 M17.521,10c0-4.148-3.373-7.521-7.521-7.521c-4.148,0-7.521,3.374-7.521,7.521c0,4.147,3.374,7.521,7.521,7.521C14.148,17.521,17.521,14.147,17.521,10"
                                    ></path>
                                </svg>
                            </span>
                        </DisclosureButton>
                        <transition
                            :show="open"
                            enter-active-class="duration-100 transition-all"
                            enter-from-class="max-h-0"
                            enter-to-class="max-h-64"
                            leave-active-class="duration-100 transition-all"
                            leave-from-class="max-h-64"
                            leave-to-class="max-h-0"
                        >
                            <DisclosurePanel
                                as="div"
                                class="max-h-0 overflow-hidden border-l-2 border-blue-600"
                            >
                                <div
                                    v-if="info.title === 'OVERALL'"
                                    class="flex flex-col overflow-auto border border-gray-200"
                                >
                                    <table
                                        id="overall-table-print"
                                        class="w-min table-auto overflow-auto rounded border border-gray-200 bg-white shadow"
                                    >
                                        <thead>
                                            <tr
                                                v-if="$page.props.can.view_sensitive_event"
                                                class="noprint border-b bg-gray-50"
                                            >
                                                <th
                                                    class="p-2 text-left text-sm font-normal"
                                                    colspan="6"
                                                >
                                                    <a
                                                        class="inline-flex pl-4 text-right"
                                                        href="#"
                                                        @click="printElement(`overall-table-print`)"
                                                    >
                                                        Print
                                                    </a>
                                                </th>
                                            </tr>
                                            <tr class="border-b bg-gray-50">
                                                <th
                                                    scope="col"
                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                >
                                                    P.
                                                </th>
                                                <th
                                                    scope="col"
                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                >
                                                    No.
                                                </th>
                                                <th
                                                    scope="col"
                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                >
                                                    &nbsp;
                                                </th>
                                                <th
                                                    scope="col"
                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                >
                                                    Competitor
                                                </th>
                                                <th
                                                    scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                >
                                                    <FwbTooltip>
                                                        <template #trigger> Time ridden</template>
                                                        <template #content>
                                                            Total riding time minus
                                                            {{
                                                                $page.props.event.include_neutralization_time
                                                                    ? ""
                                                                    : "neutralization periods and"
                                                            }}
                                                            time spent in transfer zones
                                                        </template>
                                                    </FwbTooltip>
                                                </th>
                                                <th
                                                    scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                >
                                                    Bonif./Penal.
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody class="bg-white">
                                            <tr
                                                v-for="(competitor, competitorIndex) in props.overall_results"
                                                :key="'cccx' + competitorIndex"
                                                :class="[
                                                    competitorIndex === 0
                                                        ? 'border-gray-300'
                                                        : 'border-gray-200',
                                                    'border-t',
                                                ]"
                                            >
                                                <td
                                                    class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold text-gray-500 sm:pl-6"
                                                >
                                                    {{ competitorIndex + 1 }}
                                                    <svg
                                                        v-if="competitorIndex + 1 < 4"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 24 24"
                                                        fill="currentColor"
                                                        class="inline size-5"
                                                        :class="{
                                                            'text-yellow-500': competitorIndex + 1 === 1,
                                                            'text-gray-300': competitorIndex + 1 === 2,
                                                            'text-bronze': competitorIndex + 1 === 3,
                                                        }"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M5.166 2.621v.858c-1.035.148-2.059.33-3.071.543a.75.75 0 00-.584.859 6.753 6.753 0 006.138 5.6 6.73 6.73 0 002.743 1.346A6.707 6.707 0 019.279 15H8.54c-1.036 0-1.875.84-1.875 1.875V19.5h-.75a2.25 2.25 0 00-2.25 2.25c0 .414.336.75.75.75h15a.75.75 0 00.75-.75 2.25 2.25 0 00-2.25-2.25h-.75v-2.625c0-1.036-.84-1.875-1.875-1.875h-.739a6.706 6.706 0 01-1.112-3.173 6.73 6.73 0 002.743-1.347 6.753 6.753 0 006.139-5.6.75.75 0 00-.585-.858 47.077 47.077 0 00-3.07-.543V2.62a.75.75 0 00-.658-.744 49.22 49.22 0 00-6.093-.377c-2.063 0-4.096.128-6.093.377a.75.75 0 00-.657.744zm0 2.629c0 1.196.312 2.32.857 3.294A5.266 5.266 0 013.16 5.337a45.6 45.6 0 012.006-.343v.256zm13.5 0v-.256c.674.1 1.343.214 2.006.343a5.265 5.265 0 01-2.863 3.207 6.72 6.72 0 00.857-3.294z"
                                                            clip-rule="evenodd"
                                                        />
                                                    </svg>
                                                </td>
                                                <td
                                                    class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold text-orange-400 sm:pl-6"
                                                >
                                                    {{ competitor.competitor_number || "-" }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    <span
                                                        :title="
                                                            $page.props.app.countries[
                                                                competitor.driver_country.toLowerCase()
                                                            ].name
                                                        "
                                                        :class="`border text-xl fi fi-${competitor.driver_country.toLowerCase()}`"
                                                    ></span>
                                                    <span
                                                        v-if="competitor.co_driver_country"
                                                        :title="
                                                            $page.props.app.countries[
                                                                competitor.co_driver_country.toLowerCase()
                                                            ].name
                                                        "
                                                        :class="`border text-xl fi fi-${competitor.co_driver_country.toLowerCase()}`"
                                                    ></span>
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {{ competitor.driver_full_name }}
                                                    <template v-if="competitor.co_driver_full_name">
                                                        <br />{{ competitor.co_driver_full_name }}
                                                    </template>
                                                    <br />
                                                    <template v-if="competitor.team_name">
                                                        {{ competitor.team_name }} <br />
                                                    </template>
                                                    <span class="text-xs text-gray-400">
                                                        {{ competitor.vehicle_name }}
                                                    </span>
                                                </td>
                                                <td
                                                    class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                    :title="'Overall meters: ' + competitor.overall_meters"
                                                >
                                                    <FwbTooltip>
                                                        <template #trigger>
                                                            {{ competitor.overall_duration_s }}
                                                        </template>
                                                        <template #content>
                                                            {{ competitor.overall_duration_ms }}
                                                        </template>
                                                    </FwbTooltip>
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    <div
                                                        v-if="competitor.overall_bonus_s !== 0"
                                                        class="text-green-500"
                                                    >
                                                        +{{
                                                            moment
                                                                .duration(
                                                                    Math.abs(competitor.overall_bonus_s),
                                                                    "seconds",
                                                                )
                                                                .format("hh:mm:ss", { trim: false })
                                                        }}
                                                    </div>
                                                    <div
                                                        v-if="
                                                            competitor.overall_total_penalties_s +
                                                                competitor.overall_bonus_s !==
                                                            0
                                                        "
                                                        class="text-red-500"
                                                    >
                                                        -{{
                                                            moment
                                                                .duration(
                                                                    Math.abs(
                                                                        competitor.overall_total_penalties_s +
                                                                            competitor.overall_bonus_s,
                                                                    ),
                                                                    "seconds",
                                                                )
                                                                .format("hh:mm:ss", { trim: false })
                                                        }}
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <!--                                overall ends-->
                                <!--                                overall ends-->
                                <!--                                overall ends-->
                                <!--                                overall ends-->
                                <!--                                overall ends-->
                                <!--                                overall ends-->
                                <template
                                    v-for="eventClass in event_results.classes"
                                    :key="'eid2s' + eventClass.id"
                                >
                                    <div
                                        v-if="info.title === eventClass.name"
                                        class="flex w-full flex-col overflow-auto md:w-auto"
                                    >
                                        <Disclosure
                                            v-for="(classSection, sectionIndex) in getSections(
                                                eventClass,
                                                startListSectionSelected,
                                            )"
                                            :key="'secid22222' + classSection.id"
                                            v-slot="{ openSection, closeSection }"
                                            as="div"
                                            class="overflow-hidden rounded-lg bg-white shadow-lg"
                                        >
                                            <DisclosureButton
                                                :id="'subsections-' + sectionIndex"
                                                as="div"
                                                class="grid cursor-pointer grid-cols-1"
                                            >
                                                <div
                                                    class="flex items-center border-t-2 bg-gray-50 p-4 text-left text-base font-normal"
                                                >
                                                    <div>
                                                        <span class="text-xl font-semibold">{{
                                                            classSection.name
                                                        }}</span>
                                                        ({{
                                                            convertKmAsPerTrackUnits(
                                                                getSectionDistance(classSection) / 1000,
                                                                classSection.track.units,
                                                                1,
                                                            )
                                                        }}
                                                        {{ formatUnitsHuman(classSection.track.units)
                                                        }}{{
                                                            classSection.type === "prologue"
                                                                ? ", prologue"
                                                                : ""
                                                        }})
                                                        <br />

                                                        <span class="text-sm text-gray-500">
                                                            Class: {{ eventClass.name }} ({{
                                                                classSection.competitorClassSections.length
                                                            }})</span
                                                        >
                                                    </div>
                                                    <span class="ml-6 flex h-7 items-center">
                                                        <svg
                                                            :class="
                                                                openSection
                                                                    ? 'rotate-180 duration-500'
                                                                    : 'rotate-0 duration-500'
                                                            "
                                                            class="size-7 fill-current text-blue-700"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path
                                                                d="M13.962,8.885l-3.736,3.739c-0.086,0.086-0.201,0.13-0.314,0.13S9.686,12.71,9.6,12.624l-3.562-3.56C5.863,8.892,5.863,8.611,6.036,8.438c0.175-0.173,0.454-0.173,0.626,0l3.25,3.247l3.426-3.424c0.173-0.172,0.451-0.172,0.624,0C14.137,8.434,14.137,8.712,13.962,8.885 M18.406,10c0,4.644-3.763,8.406-8.406,8.406S1.594,14.644,1.594,10S5.356,1.594,10,1.594S18.406,5.356,18.406,10 M17.521,10c0-4.148-3.373-7.521-7.521-7.521c-4.148,0-7.521,3.374-7.521,7.521c0,4.147,3.374,7.521,7.521,7.521C14.148,17.521,17.521,14.147,17.521,10"
                                                            ></path>
                                                        </svg>
                                                    </span>
                                                </div>
                                            </DisclosureButton>
                                            <transition
                                                enter-active-class="duration-100 transition-all"
                                                enter-from-class="max-h-0"
                                                enter-to-class="max-h-64"
                                                leave-active-class="duration-100 transition-all"
                                                leave-from-class="max-h-64"
                                                leave-to-class="max-h-0"
                                            >
                                                <DisclosurePanel
                                                    as="div"
                                                    class="flex max-h-0 w-full flex-col overflow-auto border-l-2 border-blue-600"
                                                >
                                                    <table
                                                        :id="`cs-${classSection.id}`"
                                                        class="w-min table-auto overflow-auto rounded border-t border-t-gray-200 bg-white tabular-nums shadow"
                                                    >
                                                        <template v-if="classSection.competitorClassSections">
                                                            <thead>
                                                                <tr class="noprint border-b bg-gray-50">
                                                                    <th
                                                                        class="p-2 text-left text-sm font-normal"
                                                                        :colspan="
                                                                            10 +
                                                                            (classSection.waypoints
                                                                                ? classSection.waypoints.length
                                                                                : 0)
                                                                        "
                                                                    >
                                                                        <Link
                                                                            :href="
                                                                                route('event.public.replay', {
                                                                                    hashid: event.hashid,
                                                                                    seo_slug: event.seo_slug,
                                                                                }) +
                                                                                '#ccs=' +
                                                                                classSection.competitorClassSections
                                                                                    .map((ccs) => ccs.id)
                                                                                    .join(',') +
                                                                                '|'
                                                                            "
                                                                        >
                                                                            <ReplayIcon
                                                                                class="size-5"
                                                                            ></ReplayIcon
                                                                            >&nbsp;view section replay
                                                                        </Link>

                                                                        <a
                                                                            v-if="
                                                                                $page.props.can
                                                                                    .view_sensitive_event
                                                                            "
                                                                            class="inline-flex pl-4 text-right"
                                                                            :href="`?excel_w_waypoints=${classSection.id}`"
                                                                            >Results as Excel with waypoints</a
                                                                        >
                                                                        <a
                                                                            v-if="
                                                                                $page.props.can
                                                                                    .view_sensitive_event
                                                                            "
                                                                            class="inline-flex pl-4 text-right"
                                                                            href="#"
                                                                            @click="
                                                                                printElement(
                                                                                    `cs-${classSection.id}`,
                                                                                )
                                                                            "
                                                                        >
                                                                            Print
                                                                        </a>

                                                                        <JetLabel
                                                                            v-if="
                                                                                !classSection.is_add_to_overall_results
                                                                            "
                                                                        >
                                                                            <div
                                                                                v-if="
                                                                                    classSection.is_add_to_overall_results ===
                                                                                    false
                                                                                "
                                                                                class="flex items-center text-sm font-normal text-gray-500"
                                                                            >
                                                                                *not included in overall results
                                                                            </div>
                                                                        </JetLabel>
                                                                    </th>
                                                                </tr>
                                                                <tr>
                                                                    <th
                                                                        scope="col"
                                                                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                                    >
                                                                        P.
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                                    >
                                                                        No.
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                                    >
                                                                        &nbsp;
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                                    >
                                                                        Competitor
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                                    >
                                                                        <span
                                                                            v-if="
                                                                                classSection.type !== 'prologue'
                                                                            "
                                                                            >Total time</span
                                                                        >
                                                                        <FwbTooltip v-else>
                                                                            <template #trigger>
                                                                                Total time *
                                                                            </template>
                                                                            <template #content>
                                                                                <div class="max-w-md">
                                                                                    With prologue multiplier
                                                                                    <br />
                                                                                    <br />
                                                                                    There are two coefficients
                                                                                    depending on the length of
                                                                                    the prologue (As per FIM
                                                                                    Prologue multiplier rules):

                                                                                    <br />
                                                                                    <br />
                                                                                    <ul class="space-y-4">
                                                                                        <li>
                                                                                            - If the length of
                                                                                            the Prologue is
                                                                                            between 2 and 10 km,
                                                                                            a multiplication
                                                                                            coefficient of 8
                                                                                            will be applied to
                                                                                            each time of this
                                                                                            Prologue.
                                                                                        </li>
                                                                                        <li>
                                                                                            - If the length of
                                                                                            the Prologue is
                                                                                            between 10 and 20
                                                                                            km, a multiplication
                                                                                            coefficient of 5
                                                                                            will be applied to
                                                                                            each time of this
                                                                                            Prologue.
                                                                                        </li>
                                                                                    </ul>
                                                                                </div>
                                                                            </template>
                                                                        </FwbTooltip>
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                                    >
                                                                        Bonif./Penal.
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                                    >
                                                                        <FwbTooltip>
                                                                            <template #trigger>
                                                                                Time ridden
                                                                            </template>
                                                                            <template #content>
                                                                                Total riding time minus
                                                                                {{
                                                                                    $page.props.event
                                                                                        .include_neutralization_time
                                                                                        ? ""
                                                                                        : "neutralization periods and"
                                                                                }}
                                                                                time spent in transfer zones
                                                                            </template>
                                                                        </FwbTooltip>
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                                    >
                                                                        <FwbTooltip>
                                                                            <template #trigger>
                                                                                *Distance
                                                                                <span class="whitespace-nowrap"
                                                                                    >{{
                                                                                        convertKmAsPerTrackUnits(
                                                                                            getSectionDistance(
                                                                                                classSection,
                                                                                            ) / 1000,
                                                                                            classSection.track
                                                                                                .units,
                                                                                            1,
                                                                                        )
                                                                                    }}
                                                                                    {{
                                                                                        formatUnitsHuman(
                                                                                            classSection.track
                                                                                                .units,
                                                                                        )
                                                                                    }}</span
                                                                                >
                                                                            </template>
                                                                            <template #content>
                                                                                Distance ridden (Alpha feature,
                                                                                could be wrong in some cases)
                                                                            </template>
                                                                        </FwbTooltip>
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                                    >
                                                                        DSS (start)
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                                    >
                                                                        ASS (finish)
                                                                    </th>
                                                                    <th
                                                                        v-for="(
                                                                            waypoint, wptIndex
                                                                        ) in filterClassSectionWaypoints(
                                                                            classSection,
                                                                        )"
                                                                        scope="col"
                                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                                    >
                                                                        {{ wptIndex + 1 }}.
                                                                        <br />
                                                                        {{ waypoint.types.join(", ") }}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody class="bg-white">
                                                                <tr
                                                                    v-if="
                                                                        classSection.competitorClassSections
                                                                            .length === 0
                                                                    "
                                                                    class="border-b bg-gray-50"
                                                                >
                                                                    <th
                                                                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold text-gray-500 sm:pl-6"
                                                                    >
                                                                        No results to display yet in this
                                                                        section.
                                                                    </th>
                                                                </tr>
                                                                <tr
                                                                    v-for="(
                                                                        competitorClassSection, competitorIndex
                                                                    ) in classSection.competitorClassSections"
                                                                    :key="'ccc' + competitorClassSection.id"
                                                                    :class="[
                                                                        competitorIndex === 0
                                                                            ? 'border-gray-300'
                                                                            : 'border-gray-200',
                                                                        'border-t',
                                                                        !competitorClassSection.time
                                                                            ? 'bg-gray-100'
                                                                            : '',
                                                                    ]"
                                                                >
                                                                    <td
                                                                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold sm:pl-6"
                                                                        :class="
                                                                            competitorClassSection.time
                                                                                ? 'text-gray-500'
                                                                                : 'text-gray-300'
                                                                        "
                                                                    >
                                                                        {{ competitorClassSection.pos }}
                                                                    </td>
                                                                    <td
                                                                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold text-orange-400 sm:pl-6"
                                                                    >
                                                                        {{
                                                                            competitorClassSection.competitor
                                                                                .competitor_number || "-"
                                                                        }}
                                                                    </td>
                                                                    <td
                                                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                                    >
                                                                        <span
                                                                            :title="
                                                                                $page.props.app.countries[
                                                                                    competitorClassSection.competitor.driver_country.toLowerCase()
                                                                                ].name
                                                                            "
                                                                            :class="`border text-xl fi fi-${competitorClassSection.competitor.driver_country.toLowerCase()}`"
                                                                        ></span>
                                                                        <span
                                                                            v-if="
                                                                                competitorClassSection
                                                                                    .competitor
                                                                                    .co_driver_country
                                                                            "
                                                                            :title="
                                                                                $page.props.app.countries[
                                                                                    competitorClassSection.competitor.co_driver_country.toLowerCase()
                                                                                ].name
                                                                            "
                                                                            :class="`border text-xl fi fi-${competitorClassSection.competitor.co_driver_country.toLowerCase()}`"
                                                                        ></span>
                                                                    </td>
                                                                    <td
                                                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                                    >
                                                                        <template
                                                                            v-if="
                                                                                competitorClassSection.meters >
                                                                                0
                                                                            "
                                                                        >
                                                                            <Link
                                                                                :href="
                                                                                    route(
                                                                                        'event.public.replay',
                                                                                        {
                                                                                            hashid: event.hashid,
                                                                                            seo_slug:
                                                                                                event.seo_slug,
                                                                                        },
                                                                                    ) +
                                                                                    '#ccs=|' +
                                                                                    competitorClassSection.id
                                                                                "
                                                                            >
                                                                                {{
                                                                                    competitorClassSection
                                                                                        .competitor
                                                                                        .driver_full_name
                                                                                }}
                                                                                <template
                                                                                    v-if="
                                                                                        competitorClassSection
                                                                                            .competitor
                                                                                            .co_driver_full_name
                                                                                    "
                                                                                >
                                                                                    <br />{{
                                                                                        competitorClassSection
                                                                                            .competitor
                                                                                            .co_driver_full_name
                                                                                    }}
                                                                                </template>
                                                                                <ReplayIcon
                                                                                    class="noprint size-5"
                                                                                ></ReplayIcon>
                                                                            </Link>
                                                                            <Link
                                                                                :href="
                                                                                    route(
                                                                                        'event.public.player',
                                                                                        {
                                                                                            hashid: event.hashid,
                                                                                            seo_slug:
                                                                                                event.seo_slug,
                                                                                        },
                                                                                    ) +
                                                                                    '#ccs=|' +
                                                                                    competitorClassSection.id
                                                                                "
                                                                            >
                                                                                <FullscreenIcon
                                                                                    class="noprint ml-3 inline size-4"
                                                                                ></FullscreenIcon>
                                                                            </Link>
                                                                        </template>
                                                                        <template v-else>
                                                                            <span class="font-semibold">
                                                                                {{
                                                                                    competitorClassSection
                                                                                        .competitor
                                                                                        .driver_full_name
                                                                                }}
                                                                            </span>
                                                                            <template
                                                                                v-if="
                                                                                    competitorClassSection
                                                                                        .competitor
                                                                                        .co_driver_full_name
                                                                                "
                                                                            >
                                                                                <br />{{
                                                                                    competitorClassSection
                                                                                        .competitor
                                                                                        .co_driver_full_name
                                                                                }}
                                                                            </template>
                                                                        </template>
                                                                        <br />
                                                                        <template
                                                                            v-if="
                                                                                competitorClassSection
                                                                                    .competitor.team_name
                                                                            "
                                                                        >
                                                                            {{
                                                                                competitorClassSection
                                                                                    .competitor.team_name
                                                                            }}
                                                                            <br />
                                                                        </template>
                                                                        <span class="text-xs text-gray-500">
                                                                            {{
                                                                                competitorClassSection
                                                                                    .competitor.vehicle_name
                                                                            }}
                                                                        </span>
                                                                    </td>
                                                                    <td
                                                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                                    >
                                                                        <FwbTooltip
                                                                            v-if="
                                                                                competitorClassSection.total_time
                                                                            "
                                                                        >
                                                                            <template #trigger>
                                                                                {{
                                                                                    moment
                                                                                        .duration(
                                                                                            competitorClassSection.total_time,
                                                                                            "milliseconds",
                                                                                        )
                                                                                        .format("hh:mm:ss", {
                                                                                            trim: false,
                                                                                        })
                                                                                }}
                                                                            </template>
                                                                            <template #content>
                                                                                {{
                                                                                    moment
                                                                                        .duration(
                                                                                            competitorClassSection.total_time,
                                                                                            "milliseconds",
                                                                                        )
                                                                                        .format(
                                                                                            "hh:mm:ss.SSS",
                                                                                            {
                                                                                                trim: false,
                                                                                            },
                                                                                        )
                                                                                }}
                                                                            </template>
                                                                        </FwbTooltip>
                                                                    </td>
                                                                    <td
                                                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                                    >
                                                                        <div
                                                                            v-if="
                                                                                competitorClassSection.bonus_s !==
                                                                                0
                                                                            "
                                                                            class="text-green-500"
                                                                        >
                                                                            +{{
                                                                                moment
                                                                                    .duration(
                                                                                        Math.abs(
                                                                                            competitorClassSection.bonus_s,
                                                                                        ),
                                                                                        "seconds",
                                                                                    )
                                                                                    .format("hh:mm:ss", {
                                                                                        trim: false,
                                                                                    })
                                                                            }}
                                                                        </div>
                                                                        <div
                                                                            v-if="
                                                                                competitorClassSection.total_penalties_s +
                                                                                    competitorClassSection.bonus_s !==
                                                                                0
                                                                            "
                                                                            class="text-red-500"
                                                                        >
                                                                            -{{
                                                                                moment
                                                                                    .duration(
                                                                                        Math.abs(
                                                                                            competitorClassSection.total_penalties_s +
                                                                                                competitorClassSection.bonus_s,
                                                                                        ),
                                                                                        "seconds",
                                                                                    )
                                                                                    .format("hh:mm:ss", {
                                                                                        trim: false,
                                                                                    })
                                                                            }}
                                                                        </div>
                                                                        <div
                                                                            class="noprint relative flex sm:static"
                                                                        >
                                                                            <TooltipIcon
                                                                                v-if="
                                                                                    shouldShowCcsPenaltiesTooltip(
                                                                                        competitorClassSection,
                                                                                    )
                                                                                "
                                                                                class="leading-2 inline-block text-black"
                                                                            >
                                                                                <ccs-penalties-tooltip-content
                                                                                    :ccs="
                                                                                        competitorClassSection
                                                                                    "
                                                                                    :penalty_types="
                                                                                        $page.props
                                                                                            .penalty_types
                                                                                    "
                                                                                    :competitor_states="
                                                                                        $page.props
                                                                                            .competitor_states
                                                                                    "
                                                                                ></ccs-penalties-tooltip-content>
                                                                            </TooltipIcon>
                                                                            <span
                                                                                v-if="
                                                                                    $page.props.can
                                                                                        .view_sensitive_event
                                                                                "
                                                                                class="px-3 text-sm"
                                                                            >
                                                                                <button
                                                                                    class="group cursor-pointer space-x-1 p-1 font-semibold md:p-0"
                                                                                    @click="
                                                                                        showCcsPenaltiesFormModal(
                                                                                            competitorClassSection,
                                                                                            classSection,
                                                                                        )
                                                                                    "
                                                                                >
                                                                                    <EditIcon
                                                                                        class="size-5"
                                                                                    ></EditIcon>
                                                                                </button>
                                                                            </span>
                                                                        </div>

                                                                        <div
                                                                            v-if="competitorClassSection.state"
                                                                        >
                                                                            {{ competitorClassSection.state }}
                                                                        </div>
                                                                    </td>
                                                                    <td
                                                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                                    >
                                                                        <FwbTooltip
                                                                            v-if="competitorClassSection.time"
                                                                        >
                                                                            <template #trigger>
                                                                                {{
                                                                                    moment
                                                                                        .duration(
                                                                                            competitorClassSection.time,
                                                                                            "milliseconds",
                                                                                        )
                                                                                        .format("hh:mm:ss", {
                                                                                            trim: false,
                                                                                        })
                                                                                }}
                                                                            </template>
                                                                            <template #content>
                                                                                {{
                                                                                    moment
                                                                                        .duration(
                                                                                            competitorClassSection.time,
                                                                                            "milliseconds",
                                                                                        )
                                                                                        .format(
                                                                                            "hh:mm:ss.SSS",
                                                                                            {
                                                                                                trim: false,
                                                                                            },
                                                                                        )
                                                                                }}
                                                                            </template>
                                                                        </FwbTooltip>
                                                                    </td>
                                                                    <td class="text-center text-sm">
                                                                        {{
                                                                            convertKmAsPerTrackUnits(
                                                                                competitorClassSection.meters /
                                                                                    1000,
                                                                                classSection.track.units,
                                                                                1,
                                                                            )
                                                                        }}
                                                                    </td>
                                                                    <td
                                                                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
                                                                    >
                                                                        <FwbTooltip
                                                                            v-if="
                                                                                competitorClassSection.start_at
                                                                            "
                                                                        >
                                                                            <template #trigger>
                                                                                {{
                                                                                    DateTime.fromSeconds(
                                                                                        Math.round(
                                                                                            new Date(
                                                                                                competitorClassSection.start_at,
                                                                                            ).getTime() / 1000,
                                                                                        ),
                                                                                    )
                                                                                        .setZone(
                                                                                            $page.props.event
                                                                                                .timezone,
                                                                                        )
                                                                                        .toLocaleString(
                                                                                            classSection.start_type ===
                                                                                                "standing"
                                                                                                ? DateTime.TIME_24_SIMPLE
                                                                                                : DateTime.TIME_24_WITH_SECONDS,
                                                                                        )
                                                                                }}
                                                                            </template>
                                                                            <template #content>
                                                                                {{
                                                                                    DateTime.fromISO(
                                                                                        competitorClassSection.start_at,
                                                                                    )
                                                                                        .setZone(
                                                                                            $page.props.event
                                                                                                .timezone,
                                                                                        )
                                                                                        .toLocaleString(
                                                                                            DateTime.DATETIME_SHORT_WITH_SECONDS,
                                                                                        )
                                                                                }}.{{
                                                                                    DateTime.fromISO(
                                                                                        competitorClassSection.start_at,
                                                                                    )
                                                                                        .setZone(
                                                                                            $page.props.event
                                                                                                .timezone,
                                                                                        )
                                                                                        .toFormat("SSS")
                                                                                }}
                                                                            </template>
                                                                        </FwbTooltip>
                                                                    </td>
                                                                    <td
                                                                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
                                                                    >
                                                                        <FwbTooltip
                                                                            v-if="
                                                                                competitorClassSection.finish_at
                                                                            "
                                                                        >
                                                                            <template #trigger>
                                                                                {{
                                                                                    DateTime.fromSeconds(
                                                                                        Math.round(
                                                                                            new Date(
                                                                                                competitorClassSection.finish_at,
                                                                                            ).getTime() / 1000,
                                                                                        ),
                                                                                    )
                                                                                        .setZone(
                                                                                            $page.props.event
                                                                                                .timezone,
                                                                                        )
                                                                                        .toLocaleString(
                                                                                            DateTime.TIME_24_WITH_SECONDS,
                                                                                        )
                                                                                }}
                                                                            </template>
                                                                            <template #content>
                                                                                {{
                                                                                    DateTime.fromISO(
                                                                                        competitorClassSection.finish_at,
                                                                                    )
                                                                                        .setZone(
                                                                                            $page.props.event
                                                                                                .timezone,
                                                                                        )
                                                                                        .toLocaleString(
                                                                                            DateTime.DATETIME_SHORT_WITH_SECONDS,
                                                                                        )
                                                                                }}.{{
                                                                                    DateTime.fromISO(
                                                                                        competitorClassSection.finish_at,
                                                                                    )
                                                                                        .setZone(
                                                                                            $page.props.event
                                                                                                .timezone,
                                                                                        )
                                                                                        .toFormat("SSS")
                                                                                }}
                                                                            </template>
                                                                        </FwbTooltip>
                                                                    </td>
                                                                    <td
                                                                        v-for="(
                                                                            wpt, wptIndex
                                                                        ) in filterClassSectionWaypoints(
                                                                            classSection,
                                                                        )"
                                                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                                        :title="
                                                                            wpt.types.includes('FN')
                                                                                ? 'In: ' +
                                                                                  findCompetitorClassSectionWaypoint(
                                                                                      competitorClassSection,
                                                                                      wpt,
                                                                                      'in_time',
                                                                                  )
                                                                                : ''
                                                                        "
                                                                    >
                                                                        {{
                                                                            findCompetitorClassSectionWaypoint(
                                                                                competitorClassSection,
                                                                                wpt,
                                                                                wpt.types.includes("FN")
                                                                                    ? "out_time"
                                                                                    : "in_time",
                                                                            )
                                                                        }}
                                                                        <br />
                                                                        <span class="tex-xs text-gray-300">
                                                                            {{
                                                                                findCompetitorClassSectionWaypoint(
                                                                                    competitorClassSection,
                                                                                    wpt,
                                                                                    wpt.types.includes("FN")
                                                                                        ? "out_ts_ms"
                                                                                        : "in_ts_ms",
                                                                                )
                                                                            }}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </template>
                                                    </table>
                                                </DisclosurePanel>
                                            </transition>
                                        </Disclosure>
                                        <Disclosure
                                            v-slot="{ openSection2, closeSection2 }"
                                            as="div"
                                            class="overflow-hidden rounded-lg bg-white shadow-lg"
                                        >
                                            <DisclosureButton
                                                as="div"
                                                class="grid cursor-pointer grid-cols-1"
                                            >
                                                <div
                                                    class="flex items-center border-t-2 bg-gray-50 p-4 text-left text-base font-normal"
                                                >
                                                    <div class="font-semibold">
                                                        Overall results: "{{ eventClass.name }}"
                                                    </div>
                                                    <span class="ml-6 flex h-7 items-center">
                                                        <svg
                                                            :class="
                                                                openSection2
                                                                    ? 'rotate-180 duration-500'
                                                                    : 'rotate-0 duration-500'
                                                            "
                                                            class="size-7 fill-current text-blue-700"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path
                                                                d="M13.962,8.885l-3.736,3.739c-0.086,0.086-0.201,0.13-0.314,0.13S9.686,12.71,9.6,12.624l-3.562-3.56C5.863,8.892,5.863,8.611,6.036,8.438c0.175-0.173,0.454-0.173,0.626,0l3.25,3.247l3.426-3.424c0.173-0.172,0.451-0.172,0.624,0C14.137,8.434,14.137,8.712,13.962,8.885 M18.406,10c0,4.644-3.763,8.406-8.406,8.406S1.594,14.644,1.594,10S5.356,1.594,10,1.594S18.406,5.356,18.406,10 M17.521,10c0-4.148-3.373-7.521-7.521-7.521c-4.148,0-7.521,3.374-7.521,7.521c0,4.147,3.374,7.521,7.521,7.521C14.148,17.521,17.521,14.147,17.521,10"
                                                            ></path>
                                                        </svg>
                                                    </span>
                                                </div>
                                            </DisclosureButton>
                                            <transition
                                                enter-active-class="duration-100 transition-all"
                                                enter-from-class="max-h-0"
                                                enter-to-class="max-h-64"
                                                leave-active-class="duration-100 transition-all"
                                                leave-from-class="max-h-64"
                                                leave-to-class="max-h-0"
                                            >
                                                <DisclosurePanel
                                                    as="div"
                                                    class="flex max-h-0 w-full flex-col overflow-auto border-l-2 border-blue-600"
                                                >
                                                    <table
                                                        v-if="
                                                            $page.props.overall_results_by_classes[
                                                                eventClass.id
                                                            ]
                                                        "
                                                        :id="`ec-print-${eventClass.id}`"
                                                        class="w-min table-auto overflow-auto rounded border border-gray-200 bg-white shadow"
                                                    >
                                                        <thead>
                                                            <tr
                                                                v-if="$page.props.can.view_sensitive_event"
                                                                class="noprint border-b bg-gray-50"
                                                            >
                                                                <th
                                                                    class="p-2 text-left text-sm font-normal"
                                                                    colspan="6"
                                                                >
                                                                    <a
                                                                        class="inline-flex pl-4 text-right"
                                                                        href="#"
                                                                        @click="
                                                                            printElement(
                                                                                `ec-print-${eventClass.id}`,
                                                                            )
                                                                        "
                                                                    >
                                                                        Print
                                                                    </a>
                                                                </th>
                                                            </tr>
                                                            <tr class="border-b bg-gray-50">
                                                                <th
                                                                    scope="col"
                                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                                >
                                                                    P.
                                                                </th>
                                                                <th
                                                                    scope="col"
                                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                                >
                                                                    No.
                                                                </th>
                                                                <th
                                                                    scope="col"
                                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                                >
                                                                    &nbsp;
                                                                </th>
                                                                <th
                                                                    scope="col"
                                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                                >
                                                                    Competitor
                                                                </th>
                                                                <th
                                                                    scope="col"
                                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                                >
                                                                    Time
                                                                </th>
                                                                <th
                                                                    scope="col"
                                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                                >
                                                                    Bonif./Penal.
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody class="bg-white">
                                                            <tr
                                                                v-for="(competitor, competitorIndex) in $page
                                                                    .props.overall_results_by_classes[
                                                                    eventClass.id
                                                                ].overall"
                                                                :key="'cccx' + competitorIndex"
                                                                :class="[
                                                                    competitorIndex === 0
                                                                        ? 'border-gray-300'
                                                                        : 'border-gray-200',
                                                                    'border-t',
                                                                ]"
                                                            >
                                                                <td
                                                                    class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold text-gray-500 sm:pl-6"
                                                                >
                                                                    {{ competitorIndex + 1 }}
                                                                    <svg
                                                                        v-if="competitorIndex + 1 < 4"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        viewBox="0 0 24 24"
                                                                        fill="currentColor"
                                                                        class="inline size-5"
                                                                        :class="{
                                                                            'text-yellow-500':
                                                                                competitorIndex + 1 === 1,
                                                                            'text-gray-300':
                                                                                competitorIndex + 1 === 2,
                                                                            'text-bronze':
                                                                                competitorIndex + 1 === 3,
                                                                        }"
                                                                    >
                                                                        <path
                                                                            fill-rule="evenodd"
                                                                            d="M5.166 2.621v.858c-1.035.148-2.059.33-3.071.543a.75.75 0 00-.584.859 6.753 6.753 0 006.138 5.6 6.73 6.73 0 002.743 1.346A6.707 6.707 0 019.279 15H8.54c-1.036 0-1.875.84-1.875 1.875V19.5h-.75a2.25 2.25 0 00-2.25 2.25c0 .414.336.75.75.75h15a.75.75 0 00.75-.75 2.25 2.25 0 00-2.25-2.25h-.75v-2.625c0-1.036-.84-1.875-1.875-1.875h-.739a6.706 6.706 0 01-1.112-3.173 6.73 6.73 0 002.743-1.347 6.753 6.753 0 006.139-5.6.75.75 0 00-.585-.858 47.077 47.077 0 00-3.07-.543V2.62a.75.75 0 00-.658-.744 49.22 49.22 0 00-6.093-.377c-2.063 0-4.096.128-6.093.377a.75.75 0 00-.657.744zm0 2.629c0 1.196.312 2.32.857 3.294A5.266 5.266 0 013.16 5.337a45.6 45.6 0 012.006-.343v.256zm13.5 0v-.256c.674.1 1.343.214 2.006.343a5.265 5.265 0 01-2.863 3.207 6.72 6.72 0 00.857-3.294z"
                                                                            clip-rule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </td>
                                                                <td
                                                                    class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold text-orange-400 sm:pl-6"
                                                                >
                                                                    {{ competitor.competitor_number || "-" }}
                                                                </td>
                                                                <td
                                                                    class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                                >
                                                                    <span
                                                                        :title="
                                                                            $page.props.app.countries[
                                                                                competitor.driver_country.toLowerCase()
                                                                            ].name
                                                                        "
                                                                        :class="`border text-xl fi fi-${competitor.driver_country.toLowerCase()}`"
                                                                    ></span>
                                                                    <span
                                                                        v-if="competitor.co_driver_country"
                                                                        :title="
                                                                            $page.props.app.countries[
                                                                                competitor.co_driver_country.toLowerCase()
                                                                            ].name
                                                                        "
                                                                        :class="`border text-xl fi fi-${competitor.co_driver_country.toLowerCase()}`"
                                                                    ></span>
                                                                </td>
                                                                <td
                                                                    class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                                >
                                                                    {{ competitor.driver_full_name }}
                                                                    <template
                                                                        v-if="competitor.co_driver_full_name"
                                                                    >
                                                                        <br />{{
                                                                            competitor.co_driver_full_name
                                                                        }}
                                                                    </template>
                                                                    <br />
                                                                    <template v-if="competitor.team_name">
                                                                        {{ competitor.team_name }} <br />
                                                                    </template>
                                                                    <span class="text-xs text-gray-400">
                                                                        {{ competitor.vehicle_name }}
                                                                    </span>
                                                                </td>
                                                                <td
                                                                    class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                                    :title="
                                                                        'Overall meters: ' +
                                                                        competitor.overall_meters
                                                                    "
                                                                >
                                                                    <FwbTooltip>
                                                                        <template #trigger>
                                                                            {{ competitor.overall_duration_s }}
                                                                        </template>
                                                                        <template #content>
                                                                            {{ competitor.overall_duration_ms }}
                                                                        </template>
                                                                    </FwbTooltip>
                                                                </td>
                                                                <td
                                                                    class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                                                >
                                                                    <div
                                                                        v-if="competitor.overall_bonus_s !== 0"
                                                                        class="text-green-500"
                                                                    >
                                                                        +{{
                                                                            moment
                                                                                .duration(
                                                                                    Math.abs(
                                                                                        competitor.overall_bonus_s,
                                                                                    ),
                                                                                    "seconds",
                                                                                )
                                                                                .format("hh:mm:ss", {
                                                                                    trim: false,
                                                                                })
                                                                        }}
                                                                    </div>
                                                                    <div
                                                                        v-if="
                                                                            competitor.overall_total_penalties_s +
                                                                                competitor.overall_bonus_s !==
                                                                            0
                                                                        "
                                                                        class="text-red-500"
                                                                    >
                                                                        -{{
                                                                            moment
                                                                                .duration(
                                                                                    Math.abs(
                                                                                        competitor.overall_total_penalties_s +
                                                                                            competitor.overall_bonus_s,
                                                                                    ),
                                                                                    "seconds",
                                                                                )
                                                                                .format("hh:mm:ss", {
                                                                                    trim: false,
                                                                                })
                                                                        }}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </DisclosurePanel>
                                            </transition>
                                        </Disclosure>
                                    </div>
                                </template>
                            </DisclosurePanel>
                        </transition>
                    </Disclosure>
                </ul>
            </div>
        </div>

        <CompetitorClassSectionPenaltiesFormModal
            v-if="$page.props.can.view_sensitive_event"
            :event="$page.props.event"
        ></CompetitorClassSectionPenaltiesFormModal>
    </EventPublicWrapper>
</template>
